import type { DispatchTripTab } from '../dispatch.paths';
import { canOfferDriverCapture } from './capture-visibility.policy';
import { isTopUpStatus } from './top-up-status.policy';

interface TripTabInput {
  tripStatus: string;
  hasDriver: boolean;
  hasDispatched: boolean;
  hasPlan: boolean;
  canDispatch: boolean;
  canTopUp: boolean;
  canViewCash: boolean;
}

/**
 * The tabs worth showing for this trip and this user. Overview is always there; the others only appear once the trip has gone and the user
 * can do something on them, so a dispatcher before dispatch (or a read-only user) never meets an empty tab. The server decides what is allowed.
 * Accessorials (#120) appear once the trip is in close-out, and stay, read-only, once it is ready to invoice.
 * The timeline (#90) is for reading, so it needs no permission beyond the page's, and appears once there is a plan to set it against or a run to show.
 */
export function availableTripTabs(input: TripTabInput): DispatchTripTab[] {
  const tabs: DispatchTripTab[] = ['overview'];
  if (input.hasDispatched) {
    if (canOfferDriverCapture({ tripStatus: input.tripStatus, hasDriver: input.hasDriver, canDispatch: input.canDispatch })) tabs.push('record');
    const canGiveTopUp = input.canTopUp && input.hasDriver && isTopUpStatus(input.tripStatus);
    if (canGiveTopUp || input.canViewCash) tabs.push('cash');
  }
  if (input.hasPlan || input.hasDispatched) tabs.push('timeline');
  if (hasAccessorials(input.tripStatus)) tabs.push('accessorials');
  return tabs;
}

function hasAccessorials(tripStatus: string): boolean {
  return tripStatus === 'close_out' || tripStatus === 'ready_to_invoice';
}
